import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import type { IconName } from '@similarweb/icons';
import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Button } from '../../../components/Button';
import { SettingsModal } from '../../../components/Header/SettingsModal';
import { useUserQuery } from '../../../features/user/useUserQuery';
import { useIsSwUserByEmail } from '../../../hooks/useIsSwUser';
import { useSettingsTracking } from '../useSettingsTracking';

import {
  StyledSettingsLinkContainer,
  StyledSettingsLinkTitle,
  StyledSettingsLinkIcon,
  StyledSettingsFooter,
} from './styles';

export const SettingsFooter = () => {
  const { t } = useTranslation();
  const isSwUser = useIsSwUserByEmail();
  const userQuery = useUserQuery();
  const [isInternalSettingsModalOpen, setIsInternalSettingsModalOpen] = useState(false);

  const handleInternalSettingsOpen = () => setIsInternalSettingsModalOpen(true);

  const handleInternalSettingsClose = () => setIsInternalSettingsModalOpen(false);

  const { handleTrackAccountClick, handleTrackTermsClick, handleTrackSupportClick, handleFeedbackClick } =
    useSettingsTracking();

  const linksConfig: { title: string; icon: IconName; link?: string; onClick: () => void }[] = [
    {
      title: 'settings.account_settings',
      icon: 'settings',
      link: 'https://account.similarweb.com/#/details',
      onClick: handleTrackAccountClick,
    },
    {
      title: 'settings.feedback',
      icon: 'feedback',
      link: 'https://forms.wix.com/r/7270062313388900939',
      onClick: handleFeedbackClick,
    },
    {
      title: 'settings.terms',
      icon: 'company',
      link: 'https://www.similarweb.com/corp/legal/sales-intelligence-extension-terms-of-service/',
      onClick: handleTrackTermsClick,
    },
    {
      title: 'settings.help_center',
      icon: 'helpcenter',
      link: 'https://support.similarweb.com/hc/en-us',
      onClick: handleTrackSupportClick,
    },
  ];

  return (
    <>
      <StyledSettingsFooter data-automation="SettingsFooter">
        {linksConfig.map(({ title, icon, link, onClick }) => (
          <StyledSettingsLinkContainer key={title}>
            <StyledSettingsLinkTitle data-automation="SettingsLink">
              <SWReactIcons iconName={icon} size="xs" iconColor={colorsPalettes.carbon[250]} />
              <span>{t(title)}</span>
            </StyledSettingsLinkTitle>
            <StyledSettingsLinkIcon href={link} target="_blank" rel="noreferrer" onMouseDown={onClick}>
              <SWReactIcons iconName="open-new-window" size="xs" iconColor={colorsPalettes.blue[500]} />
            </StyledSettingsLinkIcon>
          </StyledSettingsLinkContainer>
        ))}
        {isSwUser || userQuery.data?.isImpersonate ? (
          <Button variant="flat" data-automation="SettingsButton" onClick={handleInternalSettingsOpen}>
            Change version
          </Button>
        ) : null}
      </StyledSettingsFooter>
      <SettingsModal
        onClose={handleInternalSettingsClose}
        isOpen={isInternalSettingsModalOpen}
        data-automation="SettingsCloseModal"
      />
    </>
  );
};
